import { roundTo } from '../../../shared/chart-shared';
import type { BoxPlotColumnData } from './box-plot-chart.model';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface BoxPlotSummary {
  /** Lower whisker (min value within 1.5 × IQR of Q1) */
  readonly min: number;
  readonly q1: number;
  readonly median: number;
  readonly mean: number;
  readonly q3: number;
  /** Upper whisker (max value within 1.5 × IQR of Q3) */
  readonly max: number;
  readonly iqr: number;
  readonly outliers: number[];
}

export interface ComputedBoxPlot {
  /** Category labels (column names) */
  readonly categories: string[];
  /** Five-number summary per column: [min, Q1, median, Q3, max] */
  readonly boxData: Array<[number, number, number, number, number]>;
  /** Outlier points as [categoryIndex, value] */
  readonly outlierData: Array<[number, number]>;
  /** Full statistical summary per column */
  readonly summaries: BoxPlotSummary[];
}

export function computeBoxPlots(columns: BoxPlotColumnData[]): ComputedBoxPlot {
  const categories: string[] = [];
  const boxData: Array<[number, number, number, number, number]> = [];
  const outlierData: Array<[number, number]> = [];
  const summaries: BoxPlotSummary[] = [];

  for (const col of columns) {
    const summary = computeSummary(col.data);

    if (!summary) {
      continue;
    }

    categories.push(col.name);
    boxData.push([summary.min, summary.q1, summary.median, summary.q3, summary.max]);
    summaries.push(summary);

    for (const outlier of summary.outliers) {
      outlierData.push([boxData.length - 1, outlier]);
    }
  }

  return { categories, boxData, outlierData, summaries };
}

// ---------------------------------------------------------------------------
// Five-number summary
// ---------------------------------------------------------------------------

function computeSummary(data: number[]): BoxPlotSummary | null {
  if (data.length === 0) {
    return null;
  }

  const sorted = [...data].sort((a, b) => a - b);

  const q1 = percentile(sorted, 25);
  const median = percentile(sorted, 50);
  const q3 = percentile(sorted, 75);
  const iqr = roundTo(q3 - q1, 4);
  const lowerFence = q1 - 1.5 * iqr;
  const upperFence = q3 + 1.5 * iqr;
  const mean = roundTo(data.reduce((sum, v) => sum + v, 0) / data.length, 4);

  const outliers: number[] = [];

  for (const v of sorted) {
    if (v < lowerFence || v > upperFence) {
      outliers.push(v);
    }
  }

  const inRange = sorted.filter((v) => v >= lowerFence && v <= upperFence);
  let whiskerMin = sorted[0];
  let whiskerMax = sorted[sorted.length - 1];

  if (inRange.length > 0) {
    whiskerMin = inRange[0];
    whiskerMax = inRange[inRange.length - 1];
  }

  return { min: whiskerMin, q1, median, mean, q3, max: whiskerMax, iqr, outliers };
}

// ---------------------------------------------------------------------------
// Percentile (linear interpolation)
// ---------------------------------------------------------------------------

function percentile(sorted: number[], p: number): number {
  const index = (p / 100) * (sorted.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);

  if (lower === upper) {
    return sorted[lower];
  }

  return roundTo(sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower), 4);
}
